import { useAuth } from "@clerk/clerk-react";
import { Box, Button, IconButton, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { fetcherAuth } from "utils/api";

import { OutreachAutomation, OutreachAutomationBrand } from "../../types";
import { useEmailLimitUpgrade } from "../../useEmailLimitUpgrade";
import BrandReviewCard from "../BrandReviewCard";

const AUTOMATION_TYPE_ICONS: Record<
  string,
  { icon: string; bg: string; color: string }
> = {
  bento_database: {
    icon: "fa-solid fa-database",
    bg: "#e8f4fd",
    color: "#1976d2",
  },
  instagram_tracked_creators: {
    icon: "fa-brands fa-instagram",
    bg: "#fce8ec",
    color: "#c03d5e",
  },
  google_places: {
    icon: "fa-solid fa-location-dot",
    bg: "#d5f0e5",
    color: "#1a7a52",
  },
};

const PREVIEW_PER_PAGE = 3;

interface QueuedBrandsReviewProps {
  automations: OutreachAutomation[];
}

export default function QueuedBrandsReview({
  automations,
}: QueuedBrandsReviewProps) {
  const navigate = useNavigate();

  // Filter to non-draft automations
  const activeAutomations = automations.filter((a) => a.status !== "draft");

  return (
    <Box sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <IconButton
          onClick={() => navigate("/dashboard/autopilot")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "1px solid rgba(1,67,57,0.15)",
            color: "rgba(1,67,57,0.6)",
            "&:hover": {
              backgroundColor: "rgba(1,67,57,0.05)",
              color: "primary.main",
            },
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-arrow-left"
            sx={{ fontSize: 14 }}
          />
        </IconButton>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 0.25, md: 2 },
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: 22, color: "primary.main" }}
            >
              Queued Brands Review
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(1,67,57,0.5)",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: { xs: 0.5, md: 0 },
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-circle-info"
                sx={{ fontSize: 11 }}
              />
              Approved emails appear in{" "}
              <Link
                to="/dashboard/inbox/scheduled"
                style={{ color: "#014339", fontWeight: 600 }}
              >
                Inbox
              </Link>{" "}
              → Scheduled
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              color: "rgba(1,67,57,0.6)",
            }}
          >
            Review and manage brands scheduled to be pitched automatically
          </Typography>
        </Box>
      </Box>

      {/* Automation previews */}
      {activeAutomations.length === 0 ? (
        <Box
          sx={{
            border: "2px dashed rgba(1,67,57,0.15)",
            borderRadius: "14px",
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "rgba(1,67,57,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-clipboard-check"
              sx={{ fontSize: 24, color: "rgba(1,67,57,0.3)" }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: "rgba(1,67,57,0.6)",
            }}
          >
            No automations yet
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "rgba(1,67,57,0.4)",
              textAlign: "center",
              maxWidth: 360,
            }}
          >
            When you create automations, their discovered brands will appear
            here for review.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {activeAutomations.map((automation) => (
            <AutomationBrandsPreview
              key={automation.id}
              automation={automation}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Per-automation preview section ──────────────────────────────────────────

function AutomationBrandsPreview({
  automation,
}: {
  automation: OutreachAutomation;
}) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { tryHandleEmailLimitError, tryHandleGmailError } =
    useEmailLimitUpgrade();
  const navigate = useNavigate();

  const [brands, setBrands] = useState<OutreachAutomationBrand[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const typeConfig = AUTOMATION_TYPE_ICONS[automation.type] ?? {
    icon: "fa-solid fa-gear",
    bg: "rgba(1,67,57,0.1)",
    color: "#014339",
  };

  const fetchBrands = async (pageNum: number, append: boolean) => {
    if (!currentOrg?.id) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const url = `/api/organization/${currentOrg.id}/outreach-automations/brands?per_page=${PREVIEW_PER_PAGE}&page=${pageNum}&automation_id=${automation.id}`;
      const res = await fetcherAuth(getToken, url, "GET", {});
      const newBrands = res.outreachAutomationBrands ?? [];
      if (append) {
        setBrands((prev) => [...prev, ...newBrands]);
      } else {
        setBrands(newBrands);
      }
      setTotal(res.total ?? 0);
      setPage(pageNum);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchBrands(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, automation.id]);

  const handleApprove = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/approve-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setTotal((prev) => prev - 1);
      setAlert("Brand approved — scheduling emails!", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  const handleReject = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/reject-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setTotal((prev) => prev - 1);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  const handleApproveAllConfirm = async () => {
    if (!currentOrg?.id) return;
    setApproveAllOpen(false);
    setApprovingAll(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}/approve-all-brands`,
        "POST",
      );
      setBrands([]);
      setTotal(0);
      setAlert("All brands approved — scheduling emails!", "success");
    } catch (error) {
      if (
        !tryHandleGmailError(
          error,
          "Connect Gmail to approve autopilot brands",
        ) &&
        !tryHandleEmailLimitError(error)
      ) {
        setErrorAlert(error);
      }
    } finally {
      setApprovingAll(false);
    }
  };

  const handleSeeMore = () => {
    fetchBrands(page + 1, true);
  };

  const remaining = total - brands.length;

  return (
    <Box>
      {/* Automation header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: typeConfig.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="span"
              className={typeConfig.icon}
              sx={{ fontSize: 13, color: typeConfig.color }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "22px",
                color: "#014339",
              }}
            >
              {automation.name ?? "Untitled Automation"}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(1,67,57,0.6)",
              }}
            >
              {loading ? (
                <Skeleton
                  variant="text"
                  width={80}
                  sx={{ display: "inline-block" }}
                />
              ) : (
                <>
                  {total} brand{total !== 1 ? "s" : ""} queued
                </>
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {automation.approvalMode === "approval_required" && total > 0 && (
            <Button
              onClick={() => setApproveAllOpen(true)}
              disabled={approvingAll || loading}
              size="small"
              sx={{
                backgroundColor: "#014339",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                fontSize: 12,
                borderRadius: "8px",
                height: 28,
                px: 1.5,
                minWidth: 0,
                "&:hover": { backgroundColor: "#012e27" },
                "&:disabled": {
                  backgroundColor: "#014339",
                  opacity: 0.5,
                  color: "white",
                },
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-check-double"
                sx={{ fontSize: 10, mr: 0.75 }}
              />
              {approvingAll ? "Approving..." : "Approve All"}
            </Button>
          )}
          <Typography
            onClick={() =>
              navigate(`/dashboard/autopilot/brands-to-review/${automation.id}`)
            }
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "rgba(1,67,57,0.5)",
              cursor: "pointer",
              "&:hover": { color: "#014339" },
            }}
          >
            View All &rarr;
          </Typography>
        </Box>
      </Box>

      {/* Brand cards or empty state */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Skeleton variant="rounded" width="100%" height={80} />
          <Skeleton variant="rounded" width="100%" height={80} />
        </Box>
      ) : brands.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed rgba(1,67,57,0.15)",
            borderRadius: "10px",
            py: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "rgba(1,67,57,0.4)",
            }}
          >
            No brands queued for this automation
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {brands.map((brand) => (
            <BrandReviewCard
              key={brand.id}
              brand={brand}
              variant="full"
              onApprove={handleApprove}
              onReject={handleReject}
              isUpdating={updatingIds.has(brand.id)}
            />
          ))}

          {/* See more button */}
          {remaining > 0 && (
            <Box
              onClick={loadingMore ? undefined : handleSeeMore}
              sx={{
                py: 0.75,
                cursor: loadingMore ? "default" : "pointer",
                "&:hover": loadingMore ? {} : { opacity: 0.7 },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#014339",
                  textAlign: "center",
                  lineHeight: "16px",
                }}
              >
                {loadingMore
                  ? "Loading..."
                  : `+ ${remaining} more brand${remaining !== 1 ? "s" : ""}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <ConfirmDialog
        header="Approve All Brands?"
        subheader={
          <Typography>
            This will approve all {total} pending brand{total !== 1 ? "s" : ""}{" "}
            for <strong>{automation.name ?? "this automation"}</strong> and
            schedule their outreach emails. This action cannot be undone.
          </Typography>
        }
        open={approveAllOpen}
        handleConfirm={handleApproveAllConfirm}
        handleCancel={() => setApproveAllOpen(false)}
        handleClose={() => setApproveAllOpen(false)}
        confirmText="Approve All"
        cancelText="Cancel"
      />
    </Box>
  );
}
